/**
 * 🎓 Phase 15.1 — Tutorial Overlay (the engine)
 *
 * Renders a centered modal card with:
 *   - emoji + title + body
 *   - optional CTA button that navigates to a route (closes the
 *     tutorial on click so the user lands on the target page)
 *   - progress dots
 *   - Back / Skip / Next buttons
 *   - keyboard support: ← prev, → next, Esc skip
 *   - mobile-first: full-bleed card on ≤640px with thumb-reachable buttons
 *
 * Design language: dark glassmorphic card on top of a subtle blurred
 * backdrop — matches the RGM Studio premium aesthetic, NOT a corporate
 * coachmark library look.
 */
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTutorial } from "@/components/tutorial/TutorialProvider";
import { TUTORIAL_SECTIONS, stepsFor } from "@/components/tutorial/sectionContent";

const TutorialOverlay = () => {
  const { activeSection, activeStep, nextStep, prevStep, exitSection, skipAll } = useTutorial();
  const navigate = useNavigate();

  // Keyboard bindings.
  useEffect(() => {
    if (!activeSection) return;
    const onKey = (e) => {
      if (e.key === "Escape") { exitSection({ kind: "exited" }); }
      else if (e.key === "ArrowRight") { nextStep(); }
      else if (e.key === "ArrowLeft") { prevStep(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [activeSection, nextStep, prevStep, exitSection]);

  if (!activeSection) return null;
  const section = TUTORIAL_SECTIONS[activeSection];
  if (!section) return null;
  const steps = stepsFor(activeSection);
  const step = steps[activeStep];
  if (!step) return null;
  const isFirst = activeStep === 0;
  const isLast = activeStep === steps.length - 1;

  const handleCta = () => {
    if (!step.cta?.to) return;
    // Don't lose progress — complete the step (advances) then navigate.
    nextStep();
    navigate(step.cta.to);
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center px-3 sm:px-6"
      data-testid="tutorial-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="tutorial-step-title"
    >
      {/* Dimmer */}
      <div
        className="absolute inset-0 bg-black/75 backdrop-blur-sm"
        onClick={() => exitSection({ kind: "exited" })}
      />

      {/* Card */}
      <div
        className="relative w-full max-w-md mb-3 sm:mb-0 rounded-2xl sm:rounded-3xl border border-amber-400/30 bg-gradient-to-br from-[#181029] via-[#0d0717] to-[#0a0514] shadow-2xl shadow-amber-500/10 overflow-hidden"
        data-testid="tutorial-card"
      >
        {/* Header strip */}
        <div className="px-4 pt-4 pb-2 flex items-center gap-3 border-b border-amber-500/15">
          <span className="text-2xl leading-none">{section.emoji}</span>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.2em] font-black text-amber-300/80">
              Tutorial · {activeStep + 1}/{steps.length}
            </p>
            <p className="text-sm font-bold text-white truncate" data-testid="tutorial-section-title">
              {section.title}
            </p>
          </div>
          <button
            onClick={() => exitSection({ kind: "exited" })}
            data-testid="tutorial-close"
            aria-label="Close tutorial"
            className="text-gray-400 hover:text-white text-xl leading-none px-2 py-1 rounded-md hover:bg-white/5"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-5 sm:px-6 sm:py-6">
          <div className="flex items-start gap-3">
            <span className="text-3xl sm:text-4xl leading-none shrink-0">{step.emoji || section.emoji}</span>
            <div className="flex-1">
              <h3
                id="tutorial-step-title"
                data-testid="tutorial-step-title"
                className="text-white text-lg sm:text-xl font-black leading-snug"
              >
                {step.title}
              </h3>
              <p
                className="text-gray-300 text-sm leading-relaxed mt-2"
                data-testid="tutorial-step-body"
              >
                {step.body}
              </p>
            </div>
          </div>

          {/* CTA */}
          {step.cta?.to && (
            <button
              onClick={handleCta}
              data-testid="tutorial-step-cta"
              className="mt-4 w-full sm:w-auto px-4 py-2.5 rounded-full text-sm font-black bg-gradient-to-r from-amber-500 to-pink-500 hover:from-amber-400 hover:to-pink-400 text-white shadow-lg shadow-amber-500/30 transition-all"
            >
              {step.cta.label} →
            </button>
          )}

          {/* Progress dots */}
          <div className="flex items-center gap-1.5 mt-5 justify-center" data-testid="tutorial-progress-dots">
            {steps.map((_, i) => (
              <span
                key={i}
                className={`h-1.5 rounded-full transition-all ${
                  i === activeStep ? "w-6 bg-amber-300" :
                  i < activeStep   ? "w-1.5 bg-amber-300/60" :
                                      "w-1.5 bg-white/15"
                }`}
              />
            ))}
          </div>
        </div>

        {/* Footer buttons */}
        <div className="px-4 pb-4 pt-1 grid grid-cols-3 gap-2">
          <button
            onClick={prevStep}
            disabled={isFirst}
            data-testid="tutorial-back"
            className={`px-3 py-2.5 rounded-full text-xs font-bold border transition-all ${
              isFirst
                ? "border-white/10 text-gray-600 cursor-not-allowed"
                : "border-amber-500/30 text-amber-200 hover:bg-amber-500/10"
            }`}
          >
            ← Back
          </button>
          <button
            onClick={skipAll}
            data-testid="tutorial-skip"
            className="px-3 py-2.5 rounded-full text-xs font-bold border border-white/15 text-gray-300 hover:bg-white/5"
          >
            Skip tutorial
          </button>
          <button
            onClick={nextStep}
            data-testid="tutorial-next"
            className="px-3 py-2.5 rounded-full text-xs font-black bg-gradient-to-r from-amber-500 to-pink-500 hover:from-amber-400 hover:to-pink-400 text-white shadow-lg shadow-amber-500/25"
          >
            {isLast ? "Finish ✓" : "Next →"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TutorialOverlay;
